import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';

import { stripePromise } from '@/features/payments';
import { formatCurrency } from '@/shared/lib/currency';

import { isOverdue } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { PaymentBreakdown } from './PaymentBreakdown';
import { PaymentMethodPicker } from './PaymentMethodPicker';
import { usePayItem } from './usePayItem';

interface PayItemDialogProps {
  item: OutstandingItem;
  onClose: () => void;
}

/**
 * Pays an outstanding item, by saved card, new card or bank transfer.
 *
 * Three charges hide behind one button — annual review, form invoice and
 * standalone ASIC invoice — and they hit different endpoints. The route is
 * derived from the row rather than from which menu item was clicked, so the
 * dialog cannot be opened against the wrong endpoint. `usePayItem` owns that
 * decision; this file only arranges it on screen.
 */
export const PayItemDialog = ({ item, onClose }: PayItemDialogProps) => (
  // Elements must wrap anything calling `useStripe`, and `usePayItem` does.
  // Mounted per open, which is also what clears the card iframes: Stripe
  // exposes no imperative reset.
  <Dialog open onClose={onClose} fullWidth maxWidth="xs">
    <Elements stripe={stripePromise}>
      <PayItemForm item={item} onClose={onClose} />
    </Elements>
  </Dialog>
);

const PayItemForm = ({ item, onClose }: PayItemDialogProps) => {
  const payment = usePayItem(item, onClose);

  return (
    <>
      <DialogTitle>Pay {item.asicCompanyName}</DialogTitle>

      <DialogContent className="flex flex-col gap-4">
        {payment.error !== '' && (
          <Alert severity="error" role="alert" className="rounded-lg">
            {payment.error}
          </Alert>
        )}

        {isOverdue(item) && (
          <Alert severity="warning" className="rounded-lg">
            This payment is overdue. Bank transfers take longer over weekends and public
            holidays, so paying by card is the faster way to avoid a late fee.
          </Alert>
        )}

        <PaymentBreakdown
          lines={payment.lines}
          processingFee={payment.processingFee}
          total={payment.total}
          invoiceLinks={payment.invoiceLinks}
        />

        <PaymentMethodPicker
          method={payment.method}
          onMethodChange={payment.selectMethod}
          isCardDisabled={payment.isCardDisabled}
          cards={payment.cards}
          isLoadingCards={payment.isLoadingCards}
          selectedCard={payment.selectedCard}
          onCardChange={payment.selectCard}
          cardholderName={payment.cardholderName}
          onCardholderNameChange={payment.setCardholderName}
          saveCard={payment.saveCard}
          onSaveCardChange={payment.setSaveCard}
          bankTransfer={payment.bankTransfer}
          onBankTransferChange={payment.setBankTransfer}
          fees={payment.fees}
          completion={payment.completion}
          isSubmitting={payment.isSubmitting}
        />
      </DialogContent>

      <DialogActions className="flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end">
        <Button onClick={onClose} disabled={payment.isSubmitting} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void payment.pay()}
          disabled={!payment.canPay}
          className="w-full sm:w-auto"
        >
          {payment.isSubmitting ? 'Paying…' : `Pay ${formatCurrency(payment.total)}`}
        </Button>
      </DialogActions>
    </>
  );
};
